import { Link } from 'react-router-dom';
import { Linkedin, Twitter, Mail } from 'lucide-react';

interface TeamMember {
  id: number;
  name: string;
  role: string;
  bio: string;
  image: string;
  social: {
    linkedin?: string;
    twitter?: string;
    email?: string;
  };
}

const teamMembers: TeamMember[] = [
  {
    id: 1,
    name: 'Hamda Tariq',
    role: 'Founder & CEO',
    bio: 'With 25+ years in e-commerce, Hamda founded Dehleze to create a trusted marketplace that connects quality products with customers across the region.',
    image: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Hamda',
    social: {
      linkedin: '#',
      twitter: '#',
      email: 'amira@dehleze.com',
    },
  },
  {
    id: 2,
    name: 'Karim Al-Mansouri',
    role: 'Chief Technology Officer',
    bio: 'Leading our tech innovation, Karim ensures Dehleze remains at the forefront of e-commerce technology and user experience.',
    image: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Karim',
    social: {
      linkedin: '#',
      twitter: '#',
      email: 'karim@dehleze.com',
    },
  },
  {
    id: 3,
    name: 'Fatima Al-Dawod',
    role: 'Chief Operating Officer',
    bio: 'Fatima oversees operations and logistics, ensuring every order is fulfilled perfectly and delivered on time to our customers.',
    image: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Fatima',
    social: {
      linkedin: '#',
      twitter: '#',
      email: 'fatima@dehleze.com',
    },
  },
  {
    id: 4,
    name: 'Mohammed Saeed',
    role: 'Head of Customer Experience',
    bio: 'Mohammed leads our customer-first approach, ensuring every interaction with Dehleze exceeds expectations.',
    image: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Mohammed',
    social: {
      linkedin: '#',
      twitter: '#',
      email: 'mohammed@dehleze.com',
    },
  },
  {
    id: 5,
    name: 'Layla Ibrahim',
    role: 'Product Manager',
    bio: 'Layla drives product innovation and strategy, bringing customer insights to life through our platform.',
    image: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Layla',
    social: {
      linkedin: '#',
      twitter: '#',
      email: 'layla@dehleze.com',
    },
  },
  {
    id: 6,
    name: 'Ahmed Al-Rashid',
    role: 'Marketing Director',
    bio: 'Ahmed builds Dehleze\'s brand and connects with our community through innovative marketing strategies.',
    image: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Ahmed',
    social: {
      linkedin: '#',
      twitter: '#',
      email: 'ahmed@dehleze.com',
    },
  },
  {
    id: 7,
    name: 'Hana Al-Mansoori',
    role: 'Head of Quality Assurance',
    bio: 'Hana ensures that every product and service meets our high standards of quality and reliability.',
    image: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Hana',
    social: {
      linkedin: '#',
      twitter: '#',
      email: 'hana@dehleze.com',
    },
  },
  {
    id: 8,
    name: 'Omar Al-Habibi',
    role: 'Senior Developer',
    bio: 'Omar leads our development team, building secure and scalable solutions for millions of users.',
    image: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Omar',
    social: {
      linkedin: '#',
      twitter: '#',
      email: 'omar@dehleze.com',
    },
  },
];

export default function TeamPage() {
  return (
    <div className="min-h-screen bg-[#f5f6fb] flex flex-col">
      <main className="flex-1 w-full max-w-6xl mx-auto px-3 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="text-xs text-gray-500 mb-8">
          <Link to="/" className="hover:text-gray-700">
            Home
          </Link>
          {' / '}
          <span className="text-gray-700 font-medium">Our Team</span>
        </nav>

        {/* Header Section */}
        <section className="mb-12 text-center">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
            Meet Our Team
          </h1>
          <p className="text-gray-600 text-sm sm:text-base max-w-2xl mx-auto">
            Behind Dehleze is a passionate team of innovators, problem-solvers, and customer advocates
            dedicated to transforming online shopping.
          </p>
        </section>

        {/* Mission & Values Section */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-md border border-gray-100 text-center hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">🎯</span>
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Our Mission</h3>
            <p className="text-gray-600 text-sm">
              To create a thriving marketplace that empowers sellers and delights customers with quality products and exceptional service.
            </p>
          </div>

          <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-md border border-gray-100 text-center hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">💡</span>
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Our Values</h3>
            <p className="text-gray-600 text-sm">
              We believe in integrity, innovation, and putting customers first. Trust and transparency guide every decision we make.
            </p>
          </div>

          <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-md border border-gray-100 text-center hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">🌟</span>
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Our Vision</h3>
            <p className="text-gray-600 text-sm">
              To be the most trusted and customer-centric marketplace in the region, setting the standard for online shopping excellence.
            </p>
          </div>
        </section>

        {/* Team Members Section */}
        <section>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-8 text-center">
            Leadership Team
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {teamMembers.map((member) => (
              <div
                key={member.id}
                className="bg-white rounded-2xl overflow-hidden shadow-md border border-gray-100 hover:shadow-xl transition-all hover:-translate-y-1"
              >
                {/* Member Image */}
                <div className="h-56 bg-gradient-to-br from-orange-100 to-pink-100 overflow-hidden flex items-center justify-center">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Member Info */}
                <div className="p-5">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">
                    {member.name}
                  </h3>
                  <p className="text-orange-500 font-semibold text-sm mb-3">
                    {member.role}
                  </p>
                  <p className="text-gray-600 text-xs mb-4 line-clamp-3">
                    {member.bio}
                  </p>

                  {/* Social Links */}
                  <div className="flex gap-3 pt-3 border-t border-gray-100">
                    {member.social.linkedin && (
                      <a
                        href={member.social.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-blue-600 transition-colors"
                        aria-label="LinkedIn"
                      >
                        <Linkedin size={18} />
                      </a>
                    )}
                    {member.social.twitter && (
                      <a
                        href={member.social.twitter}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-blue-400 transition-colors"
                        aria-label="Twitter"
                      >
                        <Twitter size={18} />
                      </a>
                    )}
                    {member.social.email && (
                      <a
                        href={`mailto:${member.social.email}`}
                        className="text-gray-400 hover:text-orange-500 transition-colors"
                        aria-label="Email"
                      >
                        <Mail size={18} />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Culture Section */}
        <section className="mt-12 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 sm:p-8 border border-blue-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Our Culture
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-gray-700">
            <div>
              <p className="mb-4">
                <span className="font-semibold text-gray-900">Collaboration:</span> We work as one team toward shared goals, celebrating wins together and learning from every challenge.
              </p>
              <p>
                <span className="font-semibold text-gray-900">Customer First:</span> Every decision is guided by our commitment to serving our customers and sellers with excellence.
              </p>
            </div>
            <div>
              <p className="mb-4">
                <span className="font-semibold text-gray-900">Growth Mindset:</span> We encourage continuous learning, innovation, and professional development for all team members.
              </p>
              <p>
                <span className="font-semibold text-gray-900">Integrity:</span> We conduct business with honesty and transparency, building trust with our community every single day.
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="mt-12 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
            Join Our Growing Team
          </h2>
          <p className="text-gray-600 text-sm sm:text-base mb-6 max-w-2xl mx-auto">
            Think you'd be a great fit for Dehleze? We're always looking for talented individuals
            passionate about e-commerce and customer excellence.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="mailto:careers@dehleze.com"
              className="inline-block px-8 py-3 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 transition-colors text-center text-sm sm:text-base"
            >
              View Careers
            </a>
            <Link
              to="/contact"
              className="inline-block px-8 py-3 bg-white text-orange-500 border border-orange-500 rounded-lg font-semibold hover:bg-orange-50 transition-colors text-center text-sm sm:text-base"
            >
              Get In Touch
            </Link>
          </div>
        </section>
      </main>

     
    </div>
  );
}
